<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { usePvStore } from "@modules/pv/stores/pv.store"
import { usePvPageShell } from "@modules/pv/composables/usePvPageShell"
import type { PvValorisationTemplateRoot } from "@modules/pv/types/valorisation-pv.types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            noPdl: "Il n'y a pas de compteur électrique sur le bâtiment pour pouvoir lancer une analyse.",
            addPdl: "Ajouter un compteur électrique",
            noEtudePv:
                "Aucune étude de dimensionnement photovoltaïque n’a été réalisée pour le moment.",
            createEtudePv: "Créer une étude",
            noEtudeEco:
                "Aucune étude économique n’a été réalisée pour la dernière étude de dimensionnement photovoltaïque pour le moment.",
            fetchError: "Impossible de charger l’étude économique.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const pvStore = usePvStore()
const shell = usePvPageShell()
const buildingStore = useBuildingStore()
const {
    sourceId,
    historyEtudePv,
    isFetchingLastEtudeEcoForSource,
    hasErrorLastEtudeEcoForSource,
    isFetchingDimensionnementHistoryBySource,
} = storeToRefs(pvStore)
const { isFetchingBuildingSources } = storeToRefs(buildingStore)

/**
 * `buildingId:sourceId` avec PDL par défaut si besoin — recalculé quand les compteurs
 * sont chargés (évite un premier passage sans PDL puis aucun refetch).
 */
const getEtudeEcoLoadKey = computed((): string | null => {
    const bid = shell.getBuildingId.value
    if (!bid) return null
    if (isFetchingBuildingSources.value) return null
    if (buildingStore.hasErrorBuildingSources) return null
    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return null
    return `${bid}:${sid}`
})

/**
 * Charge la dernière étude éco pour le couple bâtiment / source.
 * L’historique de dimensionnement (et la cohérence « dernière étude ») est déjà chargé par `PageBuildingPVWrapper`.
 */
async function loadEtudeEconomiquePage(): Promise<void> {
    const bid = shell.getBuildingId.value
    if (!bid) return

    if (isFetchingBuildingSources.value) {
        return
    }
    if (buildingStore.hasErrorBuildingSources) {
        return
    }

    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) {
        pvStore.resetPvDerivedContext()
        return
    }

    if (sourceId.value !== sid) {
        pvStore.setSourceId(sid, {
            fetchSourcePv: false,
        })
    }

    const ids = { sourceId: sid, buildingId: bid }
    await pvStore.fetchLastEtudeEcoForSource(ids)
}

watch(
    getEtudeEcoLoadKey,
    (key) => {
        if (!key) return
        void loadEtudeEconomiquePage()
    },
    { immediate: true }
)

// ================================
// DONNÉES CALCULÉES
// ================================

/** `true` lorsque les chargements PV / éco nécessaires à l’écran sont en cours. */
const getIsPvEtudeEcoPageLoading = computed(() => {
    const bid = shell.getBuildingId.value
    if (!bid) return false
    if (isFetchingBuildingSources.value) return true
    const sid = sourceId.value ?? pvStore.getDefaultPdlSourceId()
    if (!sid) return false
    return (
        isFetchingLastEtudeEcoForSource.value ||
        isFetchingDimensionnementHistoryBySource.value
    )
})

/** Dernière analyse valorisation affichée. */
const getEcoAnalyse = computed(
    () => pvStore.getLastEtudeEco?.lastAnalyse ?? null
)

/** Gabarit d’affichage associé (formats des champs `data`). */
const getEcoTemplate = computed((): PvValorisationTemplateRoot | null => {
    const tpl = pvStore.getLastEtudeEco?.template
    return (tpl as PvValorisationTemplateRoot | null | undefined) ?? null
})

/**
 * Dernière analyse de dimensionnement (même ordre que `GET …/last`) : première entrée
 * de l’historique déjà chargé en parallèle par le wrapper.
 */
const getLastPvAnalyse = computed(() => historyEtudePv.value?.[0] ?? null)
</script>

<template>
    <div class="page page-building-pv-etude-economique p-0">
        <BaseSkeleton
            v-if="getIsPvEtudeEcoPageLoading"
            height="150rem"
            class="w-full rounded-md"
        />
        <template v-else>
            <BaseMessage
                v-if="hasErrorLastEtudeEcoForSource"
                severity="error"
                :closable="false"
                class="w-full"
            >
                {{ t("fetchError") }}
            </BaseMessage>
            <BaseMessage
                v-else-if="!sourceId"
                severity="info"
                :closable="false"
                class="mt-2 w-full"
            >
                <div
                    class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                >
                    <span>{{ t("noPdl") }}</span>
                    <BaseButton
                        :label="t('addPdl')"
                        icon="pi pi-plus"
                        outlined
                        class="shrink-0"
                        @click="shell.onClickAddPdl"
                    />
                </div>
            </BaseMessage>
            <BaseMessage
                v-else-if="!getLastPvAnalyse?._id"
                severity="info"
                :closable="false"
                class="w-full"
            >
                <div
                    class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
                >
                    <span>{{ t("noEtudePv") }}</span>
                    <BaseButton
                        :label="t('createEtudePv')"
                        icon="pi pi-plus"
                        outlined
                        class="shrink-0"
                        @click="shell.onClickCreateDimensionnement"
                    />
                </div>
            </BaseMessage>
            <BaseMessage
                v-else-if="!getEcoAnalyse?._id"
                severity="info"
                :closable="false"
                class="w-full"
            >
                {{ t("noEtudeEco") }}
            </BaseMessage>
            <PvEtudeEcoDisplay
                v-else
                :analyse="getEcoAnalyse"
                :eco-template="getEcoTemplate"
                :building-id="shell.getBuildingId"
                :source-id="sourceId"
            />
        </template>
    </div>
</template>
